import { useTranslation } from 'react-i18next';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import cx from 'clsx';

import { IconInputSearch, IconRcClear } from '@/assets';
import { UserAvatar, UserCard, Dropdown, Input } from '@/components';
import { useDebounceState, useSwitch, searchUserV3 } from '@/hooks';
import { useCurrentUserId, getUserNameByUserInfo, isContractByUserDesc } from '@/utils';

import style from './index.module.scss';

export const UserSearchInput = ({
  onChange,
  defaultValue,
  excludeSelf,
  className,
}: {
  onChange: (user: UserInfo | undefined) => void;
  defaultValue?: UserInfo;
  excludeSelf?: boolean;
  className?: string;
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState<string>();
  const { on: isShow, turnOn: showDropdown, turnOff: hideDropdown } = useSwitch(false);
  const userId = useCurrentUserId();
  const [value, setValue] = useDebounceState<string | undefined>(name, 500);
  const [userList, setUserList] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserInfo | undefined>(defaultValue);
  const [focus, setFocus] = useState(false);

  const handleFocus = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      setFocus(true);
      showDropdown();
    },
    [setFocus, showDropdown],
  );

  const handleBlur = () => {
    setFocus(false);
  };

  const clearSelectedUser = useCallback(() => {
    setSelectedUser(undefined);
    setName('');
    hideDropdown();
    setUserList([]);
  }, [hideDropdown]);

  useEffect(() => {
    onChange(selectedUser);
  }, [selectedUser, onChange]);

  useEffect(() => {
    setValue(name);
  }, [name, setValue]);

  useEffect(() => {
    hideDropdown();
    searchUserV3(value!, 1).then((res: any) => {
      setUserList(
        res?.users
          .filter((user: any) => {
            return (
              String(user.id).startsWith('0x') &&
              (!excludeSelf || String(user.id) !== userId) &&
              !isContractByUserDesc(user?.desc)
            );
          })
          .slice(0, 5) || [],
      );
      showDropdown();
    });
  }, [excludeSelf, showDropdown, value, hideDropdown, userId]);

  const menus = useMemo(() => {
    return userList.map(item => ({
      label: <UserCard isShowAmount data={item?.desc} />,
      onClick: () => {
        setSelectedUser(item);
        hideDropdown();
      },
    }));
  }, [hideDropdown, userList]);

  return (
    <div className={cx(style.userSearchInput, className)}>
      <Dropdown placement={'bottomRight'} trigger={['click']} visible={isShow} data={menus as any}>
        {selectedUser ? (
          <div
            className={cx(style.input, style.selectedUser, isShow && style.isFocus)}
            onClick={showDropdown}>
            <IconInputSearch className={style.iconSearch} />
            <div className={style.user}>
              <UserAvatar data={selectedUser.desc} size={28} />
              <div className={style.name}>{getUserNameByUserInfo(selectedUser, true)}</div>
            </div>
            <IconRcClear className={style.clearIcon} onClick={clearSelectedUser} />
          </div>
        ) : (
          <Input
            prefix={<IconInputSearch className={style.iconSearch} />}
            className={cx(style.input, focus && style.isFocus)}
            placeholder={t('PaidFeatureModal.searchPlaceholder')}
            value={name}
            onChange={setName}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        )}
      </Dropdown>
    </div>
  );
};
